import { Position, Point, Direction, Outside, Edge, positionKey, GridGeometry } from './GridGeometry.js';
import { serializeGrid, deserializeGridData, DEFAULT_GRID_CONFIG, GridSerializer } from './GridSerialization.js';

// Re-export for backward compatibility
export { DEFAULT_GRID_CONFIG } from './GridSerialization.js';

/**
 * Configuration for grid dimensions and digit range.
 */
export interface GridConfig {
    size: number;       // Grid dimension (6, 8, or 10)
    min: number;        // First digit (inclusive)
    max: number;        // Last digit (inclusive)
    isVortex?: boolean; // True for Vortex grids (0-K digits)
}

export class Grid {
    private digits: Map<string, number> = new Map();
    private digitPositions: Map<number, Position> = new Map();
    readonly geometry: GridGeometry;
    readonly serializer: GridSerializer;
    readonly config: GridConfig;

    constructor(config: GridConfig = DEFAULT_GRID_CONFIG) {
        this.config = { ...config };
        this.geometry = new GridGeometry(config.size);
        this.serializer = new GridSerializer(config.size);
    }

    /** Grid dimension (number of rows/columns). */
    get size(): number {
        return this.config.size;
    }

    /** First digit in the range. */
    get minDigit(): number {
        return this.config.min;
    }

    /** Last digit in the range. */
    get maxDigit(): number {
        return this.config.max;
    }

    /** Total number of digits (max - min + 1). */
    get digitCount(): number {
        return this.config.max - this.config.min + 1;
    }

    /** Get the next digit in cyclic order (wraps from max to min). */
    nextDigit(d: number): number {
        return d === this.config.max ? this.config.min : d + 1;
    }

    /** Get the previous digit in cyclic order (wraps from min to max). */
    prevDigit(d: number): number {
        return d === this.config.min ? this.config.max : d - 1;
    }

    setDigit(pos: Position, digit: number): void {
        const key = positionKey(pos);
        this.digits.set(key, digit);
        this.digitPositions.set(digit, pos);
    }

    getDigitAt(pos: Position): number | null {
        return this.digits.get(positionKey(pos)) ?? null;
    }

    getDigitPosition(digit: number): Position | null {
        return this.digitPositions.get(digit) ?? null;
    }

    hasDigitAt(pos: Position): boolean {
        return this.digits.has(positionKey(pos));
    }

    getAllDigitPositions(): Map<number, Position> {
        return new Map(this.digitPositions);
    }

    clear(): void {
        this.digits.clear();
        this.digitPositions.clear();
    }

    clone(): Grid {
        const newGrid = new Grid(this.config);
        for (const [key, digit] of this.digits) {
            const [row, col] = key.split(',').map(Number);
            newGrid.setDigit({ row, col }, digit);
        }
        return newGrid;
    }

    // === Coordinate Methods (delegated to GridGeometry) ===

    /** Check if position is valid (inside grid). */
    isValidPosition(pos: Position): boolean {
        return this.geometry.isValidPosition(pos);
    }

    /** Check if point is valid (within grid intersections). */
    isValidPoint(p: Point): boolean {
        return this.geometry.isValidPoint(p);
    }

    /** Check if point is on rim (grid boundary). */
    isRimPoint(p: Point): boolean {
        return this.geometry.isRimPoint(p);
    }

    /** Check if point is a corner of the grid. */
    isCornerPoint(p: Point): boolean {
        return this.geometry.isCornerPoint(p);
    }

    /** Every rim intersection (corners excluded by default). */
    rimPoints(includeCorners = false): Point[] {
        return this.geometry.rimPoints(includeCorners);
    }

    /** The direction pointing into the grid from a non-corner rim point (else null). */
    rimInwardDirection(p: Point): Direction | null {
        return this.geometry.rimInwardDirection(p);
    }

    /** Get the opposite rim point for wrapping (null if not on rim or at corner). */
    getWrapPoint(p: Point): Point | null {
        return this.geometry.getWrapPoint(p);
    }

    /**
     * Move position in a direction.
     * Returns Position if staying inside grid, Outside if exiting.
     */
    move(pos: Position, dir: Direction): Position | Outside {
        return this.geometry.move(pos, dir);
    }

    /**
     * Move position in direction, staying inside grid.
     * Returns null if would exit grid.
     */
    moveInside(pos: Position, dir: Direction): Position | null {
        return this.geometry.moveInside(pos, dir);
    }

    /**
     * Check if an internal edge touches the rim (has at least one endpoint on the boundary).
     */
    edgeTouchesRim(edge: { pa: Point; pb: Point }): boolean {
        return this.geometry.edgeTouchesRim(edge);
    }

    // === Edge Methods (delegated to GridGeometry) ===

    /** Create edge between two adjacent points (size-aware). */
    createEdge(p1: Point, p2: Point): Edge | null {
        return this.geometry.createEdge(p1, p2);
    }

    /**
     * Get the edge from a position in a given direction.
     * Returns the edge that separates this cell from the adjacent cell (or outside) in that direction.
     */
    getEdgeFromPosition(pos: Position, dir: Direction): Edge {
        return this.geometry.getEdgeFromPosition(pos, dir);
    }

    /**
     * Create an edge from two adjacent cells (Position or Outside).
     */
    createEdgeFromCells(ca: Position | Outside, cb: Position | Outside): Edge | null {
        return this.geometry.createEdgeFromCells(ca, cb);
    }

    /**
     * Get all edges meeting at a point (up to 4 edges, one in each direction).
     */
    getEdgesAtPoint(point: Point): Edge[] {
        return this.geometry.getEdgesAtPoint(point);
    }

    /**
     * Get all edges that share a point with this edge (i.e., edges touching this edge).
     * Returns up to 6 edges: up to 3 at each endpoint.
     */
    getEdgesTouchingEdge(edge: Edge): Edge[] {
        return this.geometry.getEdgesTouchingEdge(edge);
    }

    // === Edge Deserialization Methods (size-aware) ===

    /**
     * Deserialize edge from point-based format (size-aware).
     * Example: "0001" -> edge from point (0,0) to point (0,1)
     */
    deserializeEdge(s: string): Edge | null {
        return this.geometry.deserializeEdge(s);
    }

    /**
     * Deserialize edge from cell-based format (size-aware).
     * Internal: "A6A7" -> edge between cells A6 and A7
     * Rim: "oNA0" -> north edge of A0 (between outside and A0)
     */
    deserializeEdgeCells(s: string): Edge | null {
        return this.geometry.deserializeEdgeCells(s);
    }

    // === Serialization ===

    /**
     * Serialize the grid configuration to a canonical string format.
     * Format: "<size>/<min>-<max>:<entries>" where entries are "1A2,2J8,...9F6"
     * Each entry is: digit + column letter (A-J) + row number (0-9).
     * Digits are sorted in order (min to max).
     */
    serialize(): string {
        return serializeGrid(this.config, this.digitPositions);
    }

    /**
     * Deserialize a grid from its canonical string format.
     * Supports both new format "<size>/<min>-<max>:<entries>" and legacy format "<entries>".
     * Legacy format assumes 10/1-9 configuration.
     */
    static deserialize(serialized: string): Grid {
        const { config, positions } = deserializeGridData(serialized);
        const grid = new Grid(config);
        for (const { pos, digit } of positions) {
            grid.setDigit(pos, digit);
        }
        return grid;
    }
}
